import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { formatRelativeDate } from '@/utils/common/formatting';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SectionHeader } from '../SectionHeader';
import type { RecentActivityItem } from '@/hooks/residents/useHomeScreen';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';

const ActivityRow: React.FC<{
  item: RecentActivityItem;
  isLast: boolean;
  colors: ThemeContextValue['colors'];
  onPress: (item: RecentActivityItem) => void;
}> = React.memo(({ item, isLast, colors, onPress }) => {
  const IconComponent = item.type === 'maintenance'
    ? RESIDENTS_SVG_COMPONENTS.WRENCH
    : item.type === 'bulletin'
      ? RESIDENTS_SVG_COMPONENTS.BOARD
      : RESIDENTS_SVG_COMPONENTS.DOLLAR;

  return (
    <TouchableOpacity
      onPress={() => onPress(item)}
      activeOpacity={0.7}
      style={[styles.row, !isLast && [styles.rowBorder, { borderBottomColor: colors.separator }]]}
    >
      <View
        style={[
          styles.iconContainer,
          {
            backgroundColor: colors.activityIcon.bg,
            borderColor: colors.activityIcon.border,
          },
        ]}
      >
        <IconComponent width={18} height={18} color={colors.text.tertiary} />
      </View>
      <View style={styles.content}>
        <View style={styles.titleRow}>
          <Text style={[styles.title, { flex: 1, color: colors.text.tertiary }]} numberOfLines={1}>{item.title}</Text>
          {item.timestamp ? (
            <Text style={[styles.timestamp, { color: colors.text.dim }]}>{formatRelativeDate(item.timestamp)}</Text>
          ) : null}
        </View>
        <Text style={[styles.subtitle, { color: colors.text.dim }]} numberOfLines={1}>{item.subtitle}</Text>
      </View>
      {item.status === 'in_progress' && (
        <View style={[styles.statusBadge, { backgroundColor: colors.status.inProgress }]}>
          <Text style={[styles.statusBadgeText, { color: colors.status.inProgressText }]}>In Progress</Text>
        </View>
      )}
      {item.badgeCount != null && item.badgeCount > 0 && (
        <View style={[styles.notifBadge, { backgroundColor: colors.accent.red }]}>
          <Text style={styles.notifBadgeText}>{item.badgeCount}</Text>
        </View>
      )}
      {!item.status && !item.badgeCount && (
        <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color={colors.text.dim} />
      )}
    </TouchableOpacity>
  );
});

interface RecentActivitySectionProps {
  items: RecentActivityItem[];
  onItemPress: (item: RecentActivityItem) => void;
  maxItems?: number;
  style?: StyleProp<ViewStyle>;
}

export const RecentActivitySection: React.FC<RecentActivitySectionProps> = React.memo(({
  items,
  onItemPress,
  maxItems,
  style,
}) => {
  const { colors } = useThemeColors();

  if (items.length === 0) return null;

  const visibleItems = maxItems ? items.slice(0, maxItems) : items;

  return (
    <View style={style}>
      <SectionHeader title="Recent Activity" />
      <View>
        {visibleItems.map((item, idx) => (
          <ActivityRow
            key={item.id}
            item={item}
            isLast={idx === visibleItems.length - 1}
            colors={colors}
            onPress={onItemPress}
          />
        ))}
      </View>
    </View>
  );
});

RecentActivitySection.displayName = 'RecentActivitySection';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 16,
  },
  rowBorder: {
    borderBottomWidth: 1,
  },
  iconContainer: {
    width: REDESIGN_SPACING.iconSize.activityIcon,
    height: REDESIGN_SPACING.iconSize.activityIcon,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 2,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timestamp: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
    marginLeft: 8,
    flexShrink: 0,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    fontWeight: '500',
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  statusBadge: {
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  statusBadgeText: {
    ...REDESIGN_TYPOGRAPHY.label,
    textTransform: 'none',
    letterSpacing: 0,
  },
  notifBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
